import {
  classMap,
  css,
  html,
  nothing,
  OmniElement,
  OmniIconElement,
  OmniStyleElement,
} from 'omni-ui';

OmniStyleElement.register();
OmniIconElement.register();

/**
 * A replacement for window.alert() using an omni modal with appropriate color
 * and icon. Supports multiple types: 'info', 'success', 'warning', 'danger'.
 * 'danger' is the default.
 */
const getElem = () => {
  let elem = document.querySelector('omni-teams-message-dialog');
  if (!elem) {
    elem = document.createElement('omni-teams-message-dialog');
    document.body.appendChild(elem);
  }
  return elem;
};

export const openMessageModal = ({
  message,
  type,
  title,
  actionButtonText,
  onAction,
  closeButtonText,
  onClose,
} = {}) => {
  const elem = getElem();

  elem.dispatchEvent(
    new CustomEvent('teams-open-message-dialog', {
      detail: {
        message,
        type,
        title,
        actionButtonText,
        onAction,
        closeButtonText,
        onClose,
      },
    })
  );

  return elem;
};

class OmniTeamsMessageDialog extends OmniElement {
  static get properties() {
    return {
      isOpen: { type: Boolean },
      type: { type: String }, // info, success, warning, danger
      title: { type: String },
      message: { type: String },
      actionButtonText: { type: String },
      closeButtonText: { type: String },
      onAction: { attribute: false },
      onClose: { attribute: false },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .modal {
          top: var(--omni-app-layout-header-height, 0) !important;
        }
        .modal.is-clickthrough {
          pointer-events: none;
        }
        .modal-card {
          border-radius: 16px;
          box-shadow: 0 17px 50px 0 rgba(1, 33, 78, 0.25);
        }
        .modal-card-head {
          justify-content: space-between !important;
        }
        .modal-card-head-left {
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        .icon {
          fill: #fff;
          height: 25px !important;
          width: 25px !important;
          display: block !important;
        }
        .close-icon {
          fill: #fff;
          height: 32px;
          width: 32px;
        }
        .modal-card-head-left .icon {
          margin-right: 20px;
        }
        .modal.is-success .modal-card-head {
          background-image: var(--gradient-mint);
        }
        .modal.is-danger .modal-card-head {
          background-image: var(--gradient-melon);
        }
        .modal.is-warning .modal-card-head {
          /* TODO: Add css variable in Omni UI */
          background-image: linear-gradient(96deg, #fed882 7%, #f8d991 100%);
        }
        .modal.is-info .modal-card-head {
          background-image: var(--gradient-electric-blue);
        }
        .header-text {
          font-size: 18px;
          font-weight: 600;
          font-stretch: normal;
          font-style: normal;
          line-height: 0.78;
          letter-spacing: 0.4px;
          color: #fff;
        }
        .modal.is-warning .header-text {
          color: rgba(0, 0, 0, 0.7);
        }
        .modal.is-warning omni-icon.icon {
          fill: var(--color-almost-black) !important;
        }
        .omni button.delete {
          background-color: transparent;
          height: 32px;
          width: 32px;
          max-height: 32px;
          max-width: 32px;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 1px !important;
        }
        .message-text {
          color: rgb(74, 74, 74);
          padding-left: 45px !important;
        }
        .modal-card-foot {
          border-top: 0 !important;
          background-color: #fff !important;
          justify-content: flex-end !important;
        }
        .button.is-success:hover {
          background-image: var(--gradient-mint);
        }
        .button.is-info:hover {
          background-image: var(--gradient-electric-blue);
        }
        .button.is-danger:hover {
          background-image: var(--gradient-melon);
        }
        #secondary-action.is-warning:hover {
          /* TODO: Add CSS variable to Omni UI */
          border-color: var(--color-pale-grey-two) !important;
          background-color: var(--color-pale-grey-two) !important;
          color: var(--color-green-dark) !important;
        }
        #secondary-action.is-warning:active,
        #secondary-action.is-warning:focus {
          border-color: var(--color-blue-true80) !important;
          background-color: var(--color-blue-true80) !important;
          color: var(--color-green-dark) !important;
        }
        .omni .button.is-borderless {
          background-color: transparent;
          border: 0;
        }
        .button.is-success.is-borderless {
          color: var(--color-mint);
        }
        .button.is-info.is-borderless {
          color: var(--color-electric-blue);
        }
        .button.is-danger.is-borderless {
          color: var(--color-melon);
        }
        .button.is-warning.is-borderless {
          color: var(--color-almost-black);
        }
        #share-action-message {
          display: block;
          padding-right: 45px;
          word-break: break-all;
        }
      `,
    ];
  }

  static typeMap = {
    info: 'omni:informative:info',
    success: 'omni:informative:success',
    warning: 'omni:informative:alert',
    danger: 'omni:informative:error',
  };

  constructor() {
    super();

    this.isOpen = false;
    this.message = '';
    this.styling = '';
    this.title = '';
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('teams-open-message-dialog', this._handleOpenEvent);
  }

  disconnectedCallback() {
    this.removeEventListener(
      'teams-open-message-dialog',
      this._handleOpenEvent
    );
    super.connectedCallback();
  }

  _handleOpenEvent = ({
    detail: {
      styling = '',
      type = 'danger',
      message,
      title,
      onClose,
      closeButtonText,
      onAction,
      actionButtonText,
    },
  }) => {
    this.message = message;
    this.type = type;
    this.styling = styling;
    this.title = title;
    this.closeButtonText = closeButtonText;
    this.onClose = onClose;
    this.actionButtonText = actionButtonText;
    this.onAction = onAction;
    this.icon = OmniTeamsMessageDialog.typeMap[this.type];
    this.isOpen = true;
  };

  /**
   * Closes this modal. To avoid loops, pass `null` to override the
   * defaulted param if {@link OmniTeamsMessageDialog#onClose}
   * has been set.
   * @param {Function=} cb - A callback to invoke on close, defaulted
   * to {@link OmniTeamsMessageDialog#onClose}
   */
  closeModal = (cb = this.onClose) => {
    this.isOpen = false;

    if (typeof cb === 'function') cb(this);
  };

  // render will run automatically on state change
  render() {
    if (!this.isOpen) return nothing;

    const hasAction = !!this.onAction;
    const modalClass = `is-${this.type}`;

    // new function to avoid events being passed as method argument
    const handleClose = () => {
      this.closeModal();
    };

    return html`
      <omni-style>
        <div
          class="modal ${classMap({
            'is-active': this.isOpen,
            'is-clickthrough': !hasAction,
            [modalClass]: true,
          })}"
        >
          <!-- Display overlay if there are actions -->
          ${hasAction
            ? html`<div
                class="modal-background ${modalClass}"
                @click="${handleClose}"
                @keydown="${({ key }) => {
                  if (key === 'Enter') handleClose();
                }}"
              ></div>`
            : nothing}
          <div class="modal-card">
            <header class="modal-card-head">
              <div class="modal-card-head-left">
                <omni-icon class="icon" icon-id="${this.icon}"></omni-icon>
                <p class="header-text">${this.title}</p>
              </div>
              <div class="modal-card-head-right">
                <!-- Only display close button if no actions -->
                ${!hasAction
                  ? html`
                      <button
                        class="delete"
                        aria-label="close"
                        @click="${handleClose}"
                      >
                        <omni-icon
                          class="close-icon"
                          icon-id="omni:interactive:close"
                        ></omni-icon>
                      </button>
                    `
                  : ''}
              </div>
            </header>
            <section class="modal-card-body" style="${this.styling}">
              <p class="message-text">${this.message}</p>
            </section>
            <footer class="modal-card-foot">
              <button
                id="secondary-action"
                class="button is-borderless ${modalClass}"
                @click="${handleClose}"
              >
                ${this.closeText || 'Dismiss'}
              </button>
              ${hasAction && this.actionButtonText
                ? html`<button
                    class="button is-outlined ${modalClass}"
                    @click="${() => {
                      this.onAction(this);
                    }}"
                  >
                    ${this.actionButtonText}
                  </button>`
                : ''}
            </footer>
          </div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('omni-teams-message-dialog', OmniTeamsMessageDialog);
